import React, { useEffect, useState } from 'react';
import { Table, Container, Tabs, Tab, Pagination, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Recurso, StatusRecurso } from '../../model/Candidato/Recurso';
import useRecursoStore from '../../stores/useRecursoStore';
import JulgarRecursoModal from './JulgarRecursoModal';
import { toast } from 'react-toastify';
import { FaGavel } from 'react-icons/fa';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../utils/breadcrumbUtils';
import BackendErrorMessage from '../../components/BackendErrorMessage';

const RecursosPage: React.FC = () => {
    const { processoId, bancaId } = useParams<{ processoId: string, bancaId: string }>();
    const { recursos, getRecursos, atualizarStatusRecurso, getRecursosPendentes } = useRecursoStore();
    const [activeTab, setActiveTab] = useState<StatusRecurso>(StatusRecurso.ACEITO);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const itemsPerPage = 10;
    const [loading, setLoading] = useState<boolean>(true);
    const [recursosTratados, setRecursosTratados] = useState<Recurso[]>([]);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [recursoSelecionado, setRecursoSelecionado] = useState<Recurso | null>(null);

    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs(createBreadcrumbs.fromProcessos([
            { path: `/processos/${processoId}/bancas`, breadcrumb: 'Banca' },
            { path: `/processos/${processoId}/bancas/${bancaId}/recursos`, breadcrumb: 'Recursos' },
        ]));
    }, []);

    useEffect(() => {
        const fetchRecursos = async () => {
            try {
                setLoading(true);
                await getRecursos(bancaId!);
            } catch (error: any) {
                console.error('Erro ao buscar recursos:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchRecursos();
    }, [bancaId, getRecursos]);

    useEffect(() => {
        const filteredRecursos = recursos.filter((recurso) => recurso.status === activeTab);
        setTotalPages(Math.ceil(filteredRecursos.length / itemsPerPage));
        const paginatedRecursos = filteredRecursos.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
        setRecursosTratados(paginatedRecursos);
    }, [recursos, activeTab, currentPage, itemsPerPage]);

    const handleTabSelect = (tab: StatusRecurso) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);

    const handleAbrirModal = (recurso: Recurso) => {
        setRecursoSelecionado(recurso);
        setShowModal(true);
    };

    const handleConfirmarJulgamento = async (status: StatusRecurso) => {
        if (recursoSelecionado && recursoSelecionado.id) {
            try {
                // Chama a função da store para atualizar o status do recurso
                await atualizarStatusRecurso(recursoSelecionado.id, status);
                setShowModal(false);
                toast.success(`Recurso ${status.toLowerCase()} com sucesso!`);
            } catch (error) {
                console.error('Erro ao atualizar status do recurso:', error);
                toast.error(BackendErrorMessage(error));
            }
        }
        else {
            console.error('Erro na seleção de Recurso')
        }
    };

    const formatarData = (data: string | null): string | null => {
        if (!data) return null;
        const partes = data.split('T')[0].split('-');
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
    };

    const renderInscricoes = (candidatoEditais: any[]) => {
        if (!candidatoEditais || candidatoEditais.length === 0) {
            return '-';
        } else if (candidatoEditais.length === 1) {
            return (
                <div>
                    {candidatoEditais[0].inscricao} - {candidatoEditais[0].nomeEdital}
                </div>
            );
        } else {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`tooltip-${candidatoEditais[0].candidatoId}`}>
                            {candidatoEditais.map((edital, idx) => (
                                <div key={edital.id}>
                                    {edital.inscricao} - {edital.nomeEdital}
                                    {idx < candidatoEditais.length - 1 && <br />}
                                </div>
                            ))}
                        </Tooltip>
                    }
                >
                    <span className="badge bg-secondary cursor-pointer">{candidatoEditais.length} inscrições</span>
                </OverlayTrigger>
            );
        }
    };

    return (
        <Container className="mt-5">
            <h1 className="mb-4">Recursos</h1>
            <Tabs
                id="status-tabs"
                activeKey={activeTab}
                onSelect={(k) => handleTabSelect(k as StatusRecurso)}
                className="mb-3"
            >
                <Tab eventKey={StatusRecurso.CRIADO} title="Criado"></Tab>
                <Tab eventKey={StatusRecurso.ACEITO} title="Aceito"></Tab>
                <Tab eventKey={StatusRecurso.RECUSADO} title="Recusado"></Tab>
            </Tabs>
            {loading ? (
                <div>Carregando...</div>
            ) : (
                <>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Inscrições</th>
                                <th>Candidato</th>
                                <th>Justificativa</th>
                                <th>Data de Criação</th>
                                {activeTab === StatusRecurso.CRIADO &&
                                    <th className='text-center'>Julgar</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {recursosTratados.map((recurso) => (
                                <tr key={`recurso-${recurso.id}`}>
                                    <td>{renderInscricoes(recurso.candidatoEditais ?? [])}</td>
                                    <td>{recurso.nomeCandidato}</td>
                                    <td>{recurso.texto}</td>
                                    <td>{formatarData(recurso.dataCriacao)}</td>
                                    {activeTab === StatusRecurso.CRIADO &&
                                        <td className='text-center'>
                                            <Button variant="light" style={{ border: 'solid black 1px' }} onClick={() => handleAbrirModal(recurso)}>
                                                <FaGavel />
                                            </Button>
                                        </td>
                                    }
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination>
                        {Array.from({ length: totalPages }, (_, idx) => (
                            <Pagination.Item
                                key={`page-item-${activeTab}-${idx + 1}`}
                                active={idx + 1 === currentPage}
                                onClick={() => handlePageChange(idx + 1)}
                            >
                                {idx + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </>
            )}
            {recursoSelecionado && (
                <JulgarRecursoModal
                    recurso={recursoSelecionado}
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onConfirm={handleConfirmarJulgamento}
                />
            )}
        </Container>
    );
};

export default RecursosPage;